import { type ReactNode, useLayoutEffect } from 'react';

import { FullLogo, WilsonFullLogo } from '@revelio/assets';

import authHero from '../../../assets/images/auth-office-building-full.webp';
import { environment } from '../../../environment';
import { isWilsonBrand } from '../../../wilson-brand';
import { ThemeModeProvider, useResolvedThemeMode } from './theme';
import styles from './theme-layout.module.css';

const LOGO_HEIGHTS = [
  '22px',
  '22px',
  'clamp(20px, 2.35vh, 24px)',
  'clamp(20px, 2.64vh, 27px)',
];
// Widths track the heights above at the wordmark's 158:24 ratio, so the box is
// the artwork rather than the artwork plus trailing slack.
const LOGO_WIDTHS = [
  '145px',
  '145px',
  'clamp(132px, 15.47vh, 158px)',
  'clamp(132px, 17.38vh, 178px)',
];

// Both variants must keep the same element tree around {children}: steps
// switch variants mid-flow and a structural change would remount the form
// and drop its state.
export function ThemeLayout({
  children,
  secondaryContent,
  variant = 'split',
}: {
  children: ReactNode;
  secondaryContent?: ReactNode;
  variant?: 'split' | 'centered';
}) {
  const mode = useResolvedThemeMode();
  const isCentered = variant === 'centered';

  // Stamped on BODY, not the shell root: portalled UI (the password
  // requirements popover, toasts) renders outside this tree and must still
  // read the tokens. Light is the :root default, so only dark needs a stamp
  // and clearing it on unmount restores light.
  //
  // `data-ap-theme`, not `data-theme` — libs/ui owns the latter for the
  // dashboard and Storybook pins it to "light", which silently defeated every
  // dark auth story.
  //
  // useLayoutEffect, not useEffect: this runs before the browser paints, so a
  // direct `?theme=dark` load never shows a light frame first. The JS palette
  // this replaced resolved during render and had no such gap. Safe here — the
  // portal is a client-only SPA, so there is no SSR pass to warn about.
  useLayoutEffect(() => {
    if (mode !== 'dark') {
      return;
    }
    document.body.dataset.apTheme = 'dark';
    return () => {
      delete document.body.dataset.apTheme;
    };
  }, [mode]);

  return (
    <ThemeModeProvider value={mode}>
      <div
        className={`${styles.shell}${isCentered ? ` ${styles.centered}` : ''}`}
      >
        <div className={styles.formPane}>
          <div className={styles.column}>
            <a
              href={environment.PUBLIC_WEBSITE_URL}
              className={styles.logoLink}
            >
              {isWilsonBrand ? (
                // `viewBox` crops the leading whitespace to the "W" + dot,
                // the same treatment the Wilson login shell uses.
                <WilsonFullLogo
                  viewBox="34 0 51 13"
                  height={LOGO_HEIGHTS}
                  width={LOGO_WIDTHS}
                />
              ) : (
                <FullLogo
                  color="var(--ap-logo)"
                  height={LOGO_HEIGHTS}
                  width={LOGO_WIDTHS}
                />
              )}
            </a>
            <div className={styles.body}>{children}</div>
          </div>
        </div>
        <div className={styles.heroPane}>
          {secondaryContent ??
            (isCentered ? null : (
              <div
                className={styles.hero}
                style={{ backgroundImage: `url(${authHero})` }}
              />
            ))}
        </div>
      </div>
    </ThemeModeProvider>
  );
}
